// ABOUTME: React Query mutation hooks for creating, updating, and deleting order change lead time configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  OrderChangeLeadTimeFormValues,
  PutOrderChangeLeadTimeRequest,
  OrderChangeFieldConfigRequest,
  REFERENCE_EVENT_TO_ID,
} from '../types/orderChangeLeadTime'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

const toApiRequest = (values: OrderChangeLeadTimeFormValues): PutOrderChangeLeadTimeRequest => ({
  configurations: values.configurations.map((field): OrderChangeFieldConfigRequest => ({
    fieldCode: field.fieldCode,
    type: { isOverridden: true, value: field.type },
    autoApproveLeadTime: field.type === 'notChangeable'
      ? { isOverridden: false, value: null }
      : {
        isOverridden: true,
        value: {
          value: field.autoApproveLeadTime.value,
          uomId: field.autoApproveLeadTime.uomId,
          referenceEventId: REFERENCE_EVENT_TO_ID[field.autoApproveLeadTime.referenceEvent] ?? 2,
        },
      },
    orderManagementLeadTime: field.type === 'notChangeable'
      ? { isOverridden: false, value: null }
      : field.orderManagementLeadTime
        ? {
          isOverridden: true,
          value: {
            value: field.orderManagementLeadTime.value,
            uomId: field.orderManagementLeadTime.uomId,
            referenceEventId: REFERENCE_EVENT_TO_ID[field.orderManagementLeadTime.referenceEvent] ?? 2,
          },
        }
        : { isOverridden: false, value: null },
  })),
})

export const useCreateOrderChangeLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: OrderChangeLeadTimeFormValues }) => {
      const res = await api.post(
        `/tuc-configs/order-change-lead-times/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderChangeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderChangeLeadTime', countryId, businessLine] })
    },
  })
}

export const useCreateOrderChangeLeadTimeWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scope, values }: { scope: PendingScopeRequest; values: OrderChangeLeadTimeFormValues }) => {
      const res = await api.post(
        '/tuc-configs/order-change-lead-times',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderChangeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderChangeLeadTime', countryId, businessLine] })
    },
  })
}

export const useUpdateOrderChangeLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: OrderChangeLeadTimeFormValues }) => {
      const res = await api.put(
        `/tuc-configs/order-change-lead-times/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderChangeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderChangeLeadTime', countryId, businessLine] })
    },
  })
}

export const useDeleteOrderChangeLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/order-change-lead-times/${scopeId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderChangeLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderChangeLeadTime', countryId, businessLine] })
    },
  })
}
